<template>
  <div class="upload-video-container">
    <!-- 已上传的视频 -->
    <div v-if="videoUrl" class="video-preview">
      <video
        :src="videoUrl"
        controls
        preload="metadata"
        style="width: 100%; max-width: 300px; height: 200px;"
      >
        您的浏览器不支持视频播放
      </video>
      <div class="video-actions">
        <el-button size="mini" type="danger" @click="removeVideo">删除</el-button>
      </div>
    </div>
    <!-- 上传按钮 -->
    <div v-else v-loading="uploading" class="upload-trigger" @click="triggerUpload">
      <i class="el-icon-video-camera-solid upload-icon"></i>
      <div class="upload-text">点击上传视频</div>
      <div class="upload-tip">
        支持 {{ acceptTypes.join(', ') }} 格式，大小不超过 {{ maxSize }}MB
      </div>
    </div>
    <!-- 隐藏的文件输入框 -->
    <input
      type="file"
      ref="fileInput"
      @change="handleFileChange"
      :accept="acceptTypes.map(ext => '.'+ext).join(',')"
      style="display: none;"
    />
  </div>
</template>

<script>
export default {
  name: 'UploadVideo',
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxSize: {
      type: Number,
      default: 500 // MB
    },
    acceptTypes: {
      type: Array,
      default: () => ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv']
    }
  },
  data() {
    return {
      uploading: false,
      videoUrl: this.value
    };
  },
  watch: {
    value(newVal) {
      this.videoUrl = newVal;
    },
    videoUrl(newVal) {
      this.$emit('input', newVal);
    }
  },
  methods: {
    // 触发文件选择
    triggerUpload() {
      if (this.disabled || this.uploading) return;
      this.$refs.fileInput.click();
    },
    // 处理文件选择
    async handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      // 校验类型
      const ext = file.name.split('.').pop().toLowerCase();
      if (!this.acceptTypes.includes(ext)) {
        this.$message.error(`只能上传 ${this.acceptTypes.join(', ')} 格式视频文件!`);
        this.$refs.fileInput.value = '';
        return;
      }
      // 校验大小
      if (file.size / 1024 / 1024 > this.maxSize) {
        this.$message.error(`视频文件大小不能超过 ${this.maxSize}MB!`);
        this.$refs.fileInput.value = '';
        return;
      }
      try {
        await this.uploadVideo(file);
      } catch (error) {
        this.$message.error('上传失败，请重试');
      } finally {
        this.$refs.fileInput.value = '';
      }
    },
    // 上传视频
    async uploadVideo(file) {
      this.uploading = true;
      try {
        const fileName = this.generateFileName(file.name);
        const videoUrl = await this.uploadS3Video(file, fileName);
        this.videoUrl = videoUrl;
        this.$message.success('视频上传成功');
      } catch (error) {
        this.$message.error('视频上传失败');
      } finally {
        this.uploading = false;
      }
    },
    // S3上传
    async uploadS3Video(file, fileName) {
      const { data: resData } = await this.$get('s3/config/info', {});
      if (resData.result !== 200) throw new Error('获取S3配置失败');
      const s3Config = resData.data;
      const accountid = "05e3a3acf3a5440a0f6aa4e94818fac2";
      const accessKeyId = "adfaeaebf3a0adc777a2365aa4f7500a";
      const accessKey = "93f858b5e3e7d01f34dc25fcd0e228c61124872996059c2ec464a0431e0355b4";
      const s3 = new AWS.S3({
        params: { Bucket: "static-taotan" },
        endpoint: `https://${accountid}.r2.cloudflarestorage.com`,
        accessKeyId: `${accessKeyId}`,
        secretAccessKey: `${accessKey}`,
        signatureVersion: "v4",
      });
      const albumPhotosKey = "micros/";
      const photoKey = albumPhotosKey + fileName;
      return new Promise((resolve, reject) => {
        s3.upload(
          {
            Key: photoKey,
            Body: file,
          },
          (error, data) => {
            if (error) {
              reject(error);
            } else {
              const domain = s3Config.domain;
              const videoUrl = `${domain}/${data.key}`;
              resolve(videoUrl);
            }
          }
        );
      });
    },
    // 生成文件名
    generateFileName(originalName) {
      const data = new Date();
      const year = data.getFullYear().toString();
      const month = (data.getMonth() + 1).toString().padStart(2, '0');
      const day = data.getDate().toString().padStart(2, '0');
      const hours = data.getHours().toString().padStart(2, '0');
      const minutes = data.getMinutes().toString().padStart(2, '0');
      const seconds = data.getSeconds().toString().padStart(2, '0');
      const ms = data.getMilliseconds().toString().padStart(3, '0');
      return `${year}${month}${day}${hours}${minutes}${seconds}${ms}.${originalName.split('.').pop()}`;
    },
    // 删除视频
    removeVideo() {
      this.videoUrl = '';
    }
  }
};
</script>

<style scoped>
.upload-video-container {
  display: inline-block;
  width: 320px;
  min-height: 60px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.upload-video-container:hover {
  border-color: #409EFF;
}
.upload-trigger {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #fafafa;
  padding: 20px 0;
}
.upload-icon {
  font-size: 36px;
  color: #8c939d;
  margin-bottom: 8px;
}
.upload-text {
  font-size: 16px;
  color: #8c939d;
  margin-bottom: 8px;
}
.upload-tip {
  font-size: 12px;
  color: #999;
}
.video-preview {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  padding: 10px;
}
.video-actions {
  margin-top: 10px;
}
</style> 